import { X, Globe, Users, Edit2 } from 'lucide-react'
import type { Contact } from '@/types'

interface EmpresaContactsListProps {
  contatos: Contact[]
  onEdit: (contato: Contact) => void
  onDelete: (contatoId: string) => void
}

export function EmpresaContactsList({ contatos, onEdit, onDelete }: EmpresaContactsListProps) {
  return (
    <div className='glass-card p-0 overflow-hidden border border-[var(--glass-border)] rounded-xl'>
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='border-b border-[var(--glass-border)] bg-white/[0.02]'>
              {['Nome / Cargo', 'Contato', 'Decisor', 'Ações'].map((h) => (
                <th
                  key={h}
                  className='text-left px-5 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider text-[10px]'
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {contatos.length === 0 ? (
              <tr>
                <td colSpan={4} className='px-5 py-12 text-center'>
                  <Users size={32} className='mx-auto mb-3 opacity-20 text-[var(--font-color-muted)]' />
                  <p className='text-sm text-[var(--font-color-muted)]'>Nenhum contato cadastrado na lista.</p>
                </td>
              </tr>
            ) : (
              contatos.map((contato) => (
                <tr
                  key={contato.id}
                  className='border-b border-[var(--glass-border)] last:border-0 hover:bg-white/[0.01] transition-colors'
                >
                  <td className='px-5 py-4'>
                    <div className='flex items-center gap-3'>
                      <div className='w-8 h-8 rounded-lg bg-[var(--brand-primary-muted)] flex items-center justify-center shrink-0 overflow-hidden border border-[var(--glass-border)]'>
                        {contato.fotoUrl ? (
                          <img src={contato.fotoUrl} alt='' className='w-full h-full object-cover' />
                        ) : (
                          <span className='text-[11px] font-bold text-[var(--brand-primary)]'>
                            {(contato.nome || '??').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        )}
                      </div>
                      <div>
                        <span className='font-medium text-[var(--font-color-body)] text-sm block'>{contato.nome}</span>
                        {contato.cargo && (
                          <span className='text-[9px] font-black uppercase text-[var(--font-color-muted)] tracking-wider block mt-0.5'>
                            {contato.cargo}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className='px-5 py-4'>
                    <span className='text-[var(--font-color-body)] text-xs block'>{contato.email ?? '—'}</span>
                    {contato.telefone && (
                      <span className='text-[var(--font-color-muted)] text-[11px] block mt-0.5'>{contato.telefone}</span>
                    )}
                  </td>
                  <td className='px-5 py-4'>
                    {contato.decisor ? (
                      <span className='px-2 py-0.5 rounded-md text-[8px] font-black uppercase bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'>
                        Decisor
                      </span>
                    ) : (
                      <span className='px-2 py-0.5 rounded-md text-[8px] font-bold uppercase bg-white/[0.02] text-[var(--font-color-muted)] border border-[var(--glass-border)]'>
                        Influência
                      </span>
                    )}
                  </td>
                  <td className='px-5 py-4'>
                    <div className='flex items-center gap-2 justify-end'>
                      {contato.linkedin && (
                        <a
                          href={contato.linkedin}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='p-1.5 rounded-md text-[var(--font-color-muted)] hover:text-[var(--brand-primary)] hover:bg-white/[0.04] transition-colors border border-[var(--glass-border)]'
                        >
                          <Globe size={12} />
                        </a>
                      )}
                      <button
                        className='p-1.5 rounded-md text-[var(--font-color-muted)] hover:text-[var(--brand-primary)] hover:bg-white/[0.04] transition-colors border border-[var(--glass-border)]'
                        onClick={() => onEdit(contato)}
                        title='Editar'
                      >
                        <Edit2 size={12} />
                      </button>
                      <button
                        className='p-1.5 rounded-md text-[var(--font-color-muted)] hover:text-rose-400 hover:bg-rose-500/10 transition-colors border border-[var(--glass-border)]'
                        onClick={() => onDelete(contato.id)}
                        title='Excluir'
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
